import React, { useEffect, useState, useContext } from "react";
import { LanguageContext } from "../context/LanguageContext";
import "./object.css";

const CreateObjectStep2 = ({ formData, setFormData, onNext, onBack }) => {
  const { language, texts } = useContext(LanguageContext);

  const lang = (language || "de").split("-")[0];

  const [roomTypes, setRoomTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);

  const [showAddRoom, setShowAddRoom] = useState(false);
  const [newRoomType, setNewRoomType] = useState("");
  const [newRoomCount, setNewRoomCount] = useState(1);

  const [roomCounts, setRoomCounts] = useState({});

  // NEW: Bed modal state
  const [showAddBedModal, setShowAddBedModal] = useState(false);
  const [selectedRoomForBed, setSelectedRoomForBed] = useState(null);
  const [newBedType, setNewBedType] = useState("");
  const [newBedCount, setNewBedCount] = useState(1);
  const [newBedWidth, setNewBedWidth] = useState("");
  const [newBedLength, setNewBedLength] = useState("");


  const roomTypeEmoji = {
    1: "🛏️",
    2: "🛁",
    3: "🛋️",
    4: "🍳",
    default: "🏠"
  };

  const updateRoomCounts = (rooms) => {
    const numBedrooms = rooms.filter(r => Number(r.room_type_id) === 1).length;

    setFormData(prev => ({
      ...prev,
      rooms,
      num_bedrooms: numBedrooms
    }));
  };

  useEffect(() => {
    if (!lang) return;
    loadRoomTypes();
    loadAmenities();
    loadBedTypes();
  }, [lang]);

  const loadRoomTypes = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/room-types?lang=${lang}`);
    const data = await res.json();

    setRoomTypes(Array.isArray(data) ? data : []);

    const initial = {};
    data.forEach(rt => (initial[rt.id] = 0));
    setRoomCounts(initial);
  };

  const loadAmenities = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/room-amenities?lang=${lang}`);
    const data = await res.json();
    setAmenities(Array.isArray(data) ? data : []);
  };

  const loadBedTypes = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bed-types`);
    const data = await res.json();
    setBedTypes(Array.isArray(data) ? data : []);
  };

  // Generate rooms from counts
  const generateRoomsFromCounts = () => {
    const newRooms = [];

    roomTypes.forEach(rt => {
      const count = roomCounts[rt.id] || 0;

      for (let i = 1; i <= count; i++) {
        newRooms.push({
          id: crypto.randomUUID(),
          room_type_id: rt.id,
          index: i,
          amenities: [],
          shared: null,
          beds: [] // NEW
        });
      }
    });

    updateRoomCounts(newRooms);
  };

  const addRoom = () => {
    if (!newRoomType) return;

    const type = roomTypes.find((t) => t.id === Number(newRoomType));

    const existingRoomsOfType = (formData.rooms || []).filter(
      r => r.room_type_id === type.id
    );

    const newRooms = [];
    for (let i = 0; i < newRoomCount; i++) {
      newRooms.push({
        id: crypto.randomUUID(),
        room_type_id: type.id,
        index: existingRoomsOfType.length + i + 1,
        amenities: [],
        shared: null,
        beds: [] // NEW
      });
    }

    const updatedRooms = [...(formData.rooms || []), ...newRooms];
    updateRoomCounts(updatedRooms);

    setShowAddRoom(false);
    setNewRoomType("");
    setNewRoomCount(1);
  };

  const deleteRoom = (id) => {
    const filtered = (formData.rooms || []).filter(r => r.id !== id);

    const renumbered = filtered.map(room => {
      const roomsOfSameType = filtered.filter(
        r => r.room_type_id === room.room_type_id
      );
      const newIndex = roomsOfSameType.indexOf(room) + 1;
      return { ...room, index: newIndex };
    });

    updateRoomCounts(renumbered);
  };

  const toggleAmenity = (roomId, amenityId) => {
    const updatedRooms = formData.rooms.map((room) => {
      if (room.id !== roomId) return room;

      const has = room.amenities.includes(amenityId);

      return {
        ...room,
        amenities: has
          ? room.amenities.filter((a) => a !== amenityId)
          : [...room.amenities, amenityId]
      };
    });

    updateRoomCounts(updatedRooms);
  };

  const setSharedForRoom = (roomId, shared) => {
    const targetRoom = formData.rooms.find(r => r.id === roomId);

    const updatedRooms = formData.rooms.map(room => {
      if (room.id === roomId) {
        return { ...room, shared };
      }

      if (
        shared === true &&
        room.room_type_id === targetRoom.room_type_id
      ) {
        return { ...room, shared: false };
      }

      return room;
    });

    updateRoomCounts(updatedRooms);
  };

  const hasSharedOfType = (typeId) => {
    return (formData.rooms || []).some(
      r => r.room_type_id === typeId && r.shared === true
    );
  };

  // NEW: Add bed to room
  const addBedToRoom = () => {
    if (!selectedRoomForBed || !newBedType) return;

    const updatedRooms = formData.rooms.map(room => {
      if (room.id !== selectedRoomForBed) return room;

      return {
        ...room,
        beds: [
          ...room.beds,
          {
            id: crypto.randomUUID(),
            bed_type_id: newBedType,
            count: newBedCount,
            size_width: newBedWidth || null,
            size_length: newBedLength || null
          }
        ]
      };
    });

    setFormData(prev => ({ ...prev, rooms: updatedRooms }));

    setShowAddBedModal(false);
    setSelectedRoomForBed(null);
    setNewBedType("");
    setNewBedCount(1);
    setNewBedWidth("");
    setNewBedLength("")

  };

  const deleteBed = (roomId, bedId) => {
    const updatedRooms = formData.rooms.map(room => {
      if (room.id !== roomId) return room;

      return {
        ...room,
        beds: room.beds.filter(b => b.id !== bedId)
      };
    });

    setFormData(prev => ({ ...prev, rooms: updatedRooms }));
  };

  return (
    <div className="object-form-container">
      <h2 className="object-header">
        {texts.object.step2.title}
      </h2>

      {(formData.rooms || []).length > 0 && (
        <div className="add-room-fixed">
          <button
            className="object-button"
            onClick={() => setShowAddRoom(true)}
          >
            {texts.object.step2.addRoom}
          </button>
        </div>
      )}

      <div className="object-content-scroll-area">
        <div className="form-cards">

          {(formData.rooms || []).length === 0 && (
            <div className="empty-hint">
              <h3>{texts.object.step2.roomCountTitle}</h3>
              <p>{texts.object.step2.roomCountDescription}</p>
              <p>{texts.object.step2.roomCountSharedHint}</p>

              <div className="room-count-grid">
                {roomTypes.map(rt => (
                  <div key={rt.id} className="room-count-row">
                    <label>{rt.name}</label>
                    <input
                      type="number"
                      min="0"
                      value={roomCounts[rt.id] || 0}
                      onChange={(e) =>
                        setRoomCounts({
                          ...roomCounts,
                          [rt.id]: Number(e.target.value)
                        })
                      }
                      className="room-form-input"
                    />
                  </div>
                ))}
              </div>

              <button
                className="object-button"
                onClick={generateRoomsFromCounts}
              >
                {texts.object.step2.addAllRooms}
              </button>
            </div>
          )}

          {showAddRoom && (
            <div className="modal-overlay">
              <div className="modal-box">
                <h3>{texts.object.step2.addRoom}</h3>

                <div className="modal-row">
                  <div className="modal-field">
                    <label>{texts.object.step2.roomType}</label>
                    <select
                      value={newRoomType}
                      onChange={(e) => setNewRoomType(e.target.value)}
                      className="object-form-input"
                    >
                      <option value="">
                        {texts.object.step2.select}
                      </option>

                      {roomTypes.map((rt) => (
                        <option key={rt.id} value={rt.id}>
                          {rt.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="modal-field">
                    <label>{texts.object.step2.amount}</label>
                    <input
                      type="number"
                      min="1"
                      value={newRoomCount}
                      onChange={(e) => setNewRoomCount(Number(e.target.value))}
                      className="object-form-input"
                    />
                  </div>
                </div>

                <div className="modal-buttons">
                  <button
                    className="object-button"
                    onClick={() => setShowAddRoom(false)}
                  >
                    {texts.common.cancel}
                  </button>

                  <button
                    className="object-button modal-add-button"
                    onClick={addRoom}
                  >
                    {texts.common.add}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="room-amenities-container">
            <div className="room-grid">
              {(formData.rooms || []).map((room) => {
                const roomAmenities = amenities.filter(
                  (a) => Number(a.category_id) === Number(room.room_type_id)
                );

                const rt = roomTypes.find(r => r.id === room.room_type_id);
                const displayName = `${rt?.name || ""} ${room.index}`;

                return (
                  <div key={room.id} className="room-card improved-room-card">
                    <div className="room-card-header">
                      <div className="room-title">
                        <span className="room-icon">
                          {roomTypeEmoji[room.room_type_id] || roomTypeEmoji.default}
                        </span>
                        <h3>{displayName}</h3>
                      </div>

                      <button
                        className="delete-room-btn"
                        onClick={() => deleteRoom(room.id)}
                      >
                        ✕
                      </button>
                    </div>

                    {(room.room_type_id === 2 || room.room_type_id === 4) && (
                      <div className="shared-dropdown">
                        <label>{texts.object.step2.usage}</label>

                        <select
                          className="object-form-input"
                          value={room.shared ? "shared" : "private"}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSharedForRoom(room.id, value === "shared");
                          }}
                        >
                          <option value="private">
                            {texts.object.step2.private}
                          </option>

                          <option
                            value="shared"
                            disabled={
                              hasSharedOfType(room.room_type_id) && room.shared !== true
                            }
                          >
                            {texts.object.step2.shared}
                            {hasSharedOfType(room.room_type_id) && room.shared !== true
                              ? ` (${texts.object.step2.onlyOneAllowed})`
                              : ""}
                          </option>
                        </select>
                      </div>
                    )}

                    <div className="amenities-grid-4col">
                      {roomAmenities.map((a) => (
                        <label key={a.id} className="amenity-checkbox">
                          <input
                            type="checkbox"
                            checked={room.amenities.includes(a.id)}
                            onChange={() => toggleAmenity(room.id, a.id)}
                          />
                          {a.name}
                        </label>
                      ))}
                    </div>

                    {/* ⭐ NEW: Beds Section */}
                    {(room.room_type_id === 1 || room.room_type_id === 3) && (
                      <div className="beds-section">
                        <h4>{texts.object.step2.beds}</h4>

                        {room.beds.length > 0 && (
                          <ul className="bed-list">
                            {room.beds.map(bed => {
                              const bt = bedTypes.find(t => t.id === bed.bed_type_id);
                              return (
                                <li key={bed.id} className="bed-item">
                                  {bt ? bt[`name_${lang}`] : "?"} – {bed.count}×
                                  {(bed.size_width || bed.size_length) && (
                                  <> ({bed.size_width || "?"}×{bed.size_length || "?"} cm)</>
                                  )}

                                  <button
                                    className="delete-bed-btn"
                                    onClick={() => deleteBed(room.id, bed.id)}
                                  >
                                    ✕
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        <button
                          className="object-button small"
                          onClick={() => {
                            setSelectedRoomForBed(room.id);
                            setShowAddBedModal(true);
                          }}
                        >
                          {texts.object.step2.addBed}
                        </button>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      {/* ⭐ Add Bed Modal */}
      {showAddBedModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>{texts.object.step2.addBed}</h3>

            <div className="modal-field">
              <label>{texts.object.step2.bedType}</label>
              <select
                value={newBedType}
                onChange={(e) => setNewBedType(e.target.value)}
                className="object-form-input"
              >
                <option value="">{texts.object.step2.select}</option>
                {bedTypes.map(bt => (
                  <option key={bt.id} value={bt.id}>
                    {bt[`name_${lang}`]}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-field">
              <label>{texts.object.step2.amount}</label>
              <input
                type="number"
                min="1"
                value={newBedCount}
                onChange={(e) => setNewBedCount(Number(e.target.value))}
                className="object-form-input"
              />
            </div>

            <div className="modal-field">
              <label>{texts.object.step2.size} ({texts.object.step2.sizeOptional})</label>

              <div style={{ display: "flex", gap: "10px" }}>
                <input
                  type="number"
                  placeholder={texts.object.step2.sizeWidth}
                  value={newBedWidth}
                  onChange={(e) => setNewBedWidth(e.target.value)}
                  className="object-form-input"
                />

                <input
                  type="number"
                  placeholder={texts.object.step2.sizeLength}
                  value={newBedLength}
                  onChange={(e) => setNewBedLength(e.target.value)}
                  className="object-form-input"
                />
              </div>
            </div>


            <div className="modal-buttons">
              <button
                className="object-button"
                onClick={() => setShowAddBedModal(false)}
              >
                {texts.common.cancel}
              </button>

              <button
                className="object-button modal-add-button"
                onClick={addBedToRoom}
              >
                {texts.common.add}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreateObjectStep2;
